import { Box, Paper, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WYMAGA UWAGI — krótka lista rzeczy do zrobienia, nie kolejny kanał powiadomień
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Ograniczenie do TRZECH pozycji jest celowe i twarde. Lista, która rośnie bez ograniczeń,
 * przestaje być listą rzeczy do zrobienia i staje się kolejnym strumieniem, który się ignoruje —
 * a wtedy przestaje działać także dla tych trzech spraw, które naprawdę były ważne.
 *
 * Każda pozycja PROWADZI DO DANYCH. Alert, którego nie da się kliknąć i sprawdzić, zmusza
 * użytkownika do szukania po omacku i po kilku takich przypadkach traci wiarygodność.
 */

export type PozycjaUwagi = {
  waga: 'krytyczna' | 'uwaga' | 'informacja';
  tytul: string;
  detal: string;
  /** Trasa wewnątrz Control Center — nie adres zewnętrzny. */
  cel: string;
};

const BARWA: Record<PozycjaUwagi['waga'], string> = {
  krytyczna: 'var(--krytyczny)',
  uwaga: 'var(--uwaga)',
  informacja: 'var(--neutralny)',
};

export function WymagaUwagi({ pozycje }: { pozycje: readonly PozycjaUwagi[] }) {
  // Trzy najważniejsze, po wadze. Reszta nie znika z produktu — jest na ekranach, których dotyczy.
  const kolejnosc = { krytyczna: 0, uwaga: 1, informacja: 2 } as const;
  const widoczne = [...pozycje].sort((a, b) => kolejnosc[a.waga] - kolejnosc[b.waga]).slice(0, 3);

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
        Trzy rzeczy
      </Typography>
      {/*
        TYTUŁ KARTY, NIE TYTUŁ EKRANU. Makieta daje `.karta-tytul` 16 px krojem marki; produkt
        miał `h5` = 24 px, czyli dwa nagłówki konkurujące z tytułem ekranu (34 px). Hierarchia
        spłaszczała się dokładnie tam, gdzie miała być najostrzejsza.
      */}
      {/*
        `h5`, nie `h2`. Pierwsza wersja tej poprawki brała `.karta-tytul` z makiety za RANGĘ,
        a to jest STYL: 16 px krojem marki. Poziom nagłówka wynika z miejsca w dokumencie —
        „Wymaga uwagi" stoi obok „Źródła danych", więc musi mieć ten sam poziom. Podniesienie
        go do h2 dało przeskok h2→h5 na ekranie Jakość danych i złapał to test hierarchii.
      */}
      <Typography
        component="h2"
        sx={{
          mb: 2,
          fontFamily: 'var(--krój-marka)',
          fontWeight: 900,
          fontSize: 'var(--st-sredni)',
          letterSpacing: '-0.01em',
        }}
      >
        Wymaga uwagi
      </Typography>

      {widoczne.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2.5, borderStyle: 'dashed' }}>
          <Typography variant="body2" color="text.secondary">
            Nic nie wymaga dziś uwagi w danych, które Control Center widzi. To nie znaczy, że nic się
            nie dzieje — znaczy, że w podłączonych źródłach nie ma sygnału przekraczającego próg.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {widoczne.map((p) => (
            <Box
              key={p.tytul}
              component={Link}
              to={p.cel}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 1.5,
                p: 1.75,
                borderRadius: 'var(--r-maly)',
                background: 'var(--powierzchnia-2)',
                textDecoration: 'none',
                color: 'inherit',
                transition: 'background var(--czas-szybko), transform var(--czas-szybko)',
                '&:hover': { background: 'var(--tlo-glebokie)', transform: 'translateX(3px)' },
              }}
            >
              <Box sx={{ width: 4, borderRadius: '2px', background: BARWA[p.waga], flex: 'none' }} />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.tytul}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--tekst-slaby)' }}>{p.detal}</Typography>
              </Box>
              <Box aria-hidden sx={{ color: 'var(--tekst-slaby)', alignSelf: 'center' }}>
                →
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
